import { i18n } from "../i18n/i18n";

import React, { Component } from 'react'
import { findIndex, map, pullAt } from 'lodash'
import { produce } from 'immer'
import confirmService from '../services/confirm.service'
import locationService from '../services/location.service'
import workOrderService from '../services/workOrder.service'
import geolocationService from '../services/geolocation.service'
import palletService from '../services/pallet.service'
import alertService from '../services/alert.service'
import locationApi, { LocationData } from '../apis/location.api'
import workOrderApi, { WorkOrderData } from '../apis/workOrder.api'
import palletApi, {
  PalletData, PalletListByCodesRequest
} from '../apis/pallet.api'
import csvApi, { CsvParseRequest } from '../apis/csv.api'
import transferApi, { TransferBulkRequest } from '../apis/transfer.api'
import { ConfirmParams } from '../components/Confirm/Confirm.state'
import { ActionName } from '../components/Actions'
import Card from '../components/Card'
import CardButton from '../components/CardButton'
import ContentLayout from '../components/ContentLayout'
import ContentMain from '../components/ContentMain'
import InputFile from '../components/InputFile'
import TemplateDownloader from '../components/TemplateDownloader'
import InputText from '../components/InputText'
import PageHeading from '../components/PageHeading'
import Row from '../components/Row/Row'
import Select from '../components/Select'
import TableHorizontal, {
  TableHorizontalHeader, TableHorizontalData
} from '../components/TableHorizontal'
import SearchPicker, { SearchPickerOption } from '../components/SearchPicker'

interface BulkTransferCsvData {
  pallet: string
}

interface BulkTransferProps {}

interface BulkTransferState {
  file: File | null
  filename: string
  location: string
  bin: string
  workOrder: string
  workOrderId: number | null
  latitude: number | null
  longitude: number | null
  pallets: TableHorizontalData<PalletData>[]
  locations: LocationData[]
  workOrders: WorkOrderData[]
}

export default class BulkTransfer
  extends Component<BulkTransferProps, BulkTransferState> {
  tableHeaders: TableHorizontalHeader[] = [
    { heading: i18n("Pallet"), path: 'code' },
    { heading: i18n("Item code"), path: 'item.code' },
    { heading: i18n("Description"), path: 'item.description' },
    { heading: i18n("Quantity"), path: 'quantity' },
    { heading: i18n("Actions") }
  ]

  constructor (props: BulkTransferProps) {
    super(props)
    this.state = {
      file: null,
      filename: '',
      location: '',
      bin: '0',
      workOrder: '',
      workOrderId: null,
      latitude: null,
      longitude: null,
      pallets: [],
      locations: [],
      workOrders: []
    }
    this.handleFileChange = this.handleFileChange.bind(this)
    this.handleActionClick = this.handleActionClick.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleWorkOrderSearch = this.handleWorkOrderSearch.bind(this)
    this.handleWorkOrderSelect = this.handleWorkOrderSelect.bind(this)
    this.handleWorkOrderBlur = this.handleWorkOrderBlur.bind(this)
    this.handleTransferClick = this.handleTransferClick.bind(this)
  }

  get locationSelectOptions () {
    return locationService.selectOptions(this.state.locations)
  }

  get location () {
    const { locations, location } = this.state
    if (!location) return
    return locationService.find(locations, Number(location))
  }

  get workOrderOptions () {
    return workOrderService.searchOptions(this.state.workOrders)
  }

  listPalletByCodes (codes: string[]) {
    const payload: PalletListByCodesRequest = {}
    if (codes) payload.codes = codes
    palletApi.listByCodes(payload, pallets => {
      if (!pallets) return
      const palletsData = palletService.bulkTransferTableHorizontalData(pallets)
      this.setState({ pallets: palletsData })
    })
  }

  handleFileChange (name: string, file: File | null, filename: string) {
    this.setState({ file, filename })
    const payload: CsvParseRequest = {}
    if (file) payload.csv = file
    csvApi.parse<BulkTransferCsvData[]>(payload, pallets => {
      if (!pallets) return
      const palletCodes = map(pallets, pallet => pallet.pallet)
      this.listPalletByCodes(palletCodes)
    })
  }

  handleActionClick (id: number, action: ActionName) {
    if (action === 'delete') {
      const confirm: ConfirmParams = {
        id,
        type: 'warning',
        question: i18n('Are you sure to delete the inventory?'),
        confirmText: i18n('Yes'),
        cancelText: i18n('No'),
        onConfirm: () => this.handleDeleteConfirm(id)
      }
      confirmService.push(confirm)
    }
  }

  handleDeleteConfirm (id: number) {
    this.setState(state => {
      return produce(state, draft => {
        const index = findIndex(draft.pallets, { id })
        pullAt(draft.pallets, index)
      })
    })
  }

  handleInputChange (name: string, value: string) {
    if (name === 'location') this.setState({ location: value })
    else if (name === 'bin') this.setState({ bin: value })
    else if (name === 'workOrder') {
      this.setState({ workOrderId: null, workOrder: value })
    }
  }

  handleWorkOrderSearch (name: string, code: string) {
    workOrderApi.searchByCode(code, workOrders => {
      if (!workOrders) return
      this.setState({ workOrders })
    })
  }

  handleWorkOrderSelect (name: string, option: SearchPickerOption) {
    const workOrderId = option.id as number
    this.setState({
      workOrder: option.value,
      workOrderId,
      workOrders: []
    })
  }

  handleWorkOrderBlur (name: string) {
    this.setState({ workOrders: [] })
  }

  resetState () {
    this.setState({
      file: null,
      filename: '',
      location: '',
      bin: '0',
      workOrder: '',
      workOrderId: null,
      pallets: []
    })
  }

  handleTransferConfirm () {
    const { location, bin, workOrderId } = this.state
    const { latitude, longitude, pallets } = this.state
    const locationData = this.location
    const isProductionLocation = locationData && locationData.type && locationData.type.key === 'production'
    const payload: TransferBulkRequest = {}
    if (location) payload.location = Number(location)
    if (bin) payload.bin = bin
    if (workOrderId && isProductionLocation) payload.workOrder = workOrderId
    if (latitude) payload.latitude = latitude
    if (longitude) payload.longitude = longitude
    if (longitude) payload.longitude = longitude
    if (pallets) payload.pallets = palletService.listIds(pallets)
    transferApi.bulk(payload, pallets => {
      if (!pallets) return
      alertService.success('Successfully transferred')
      this.resetState()
    })
  }

  handleTransferClick () {
    const confirm: ConfirmParams = {
      id: 1,
      question: i18n('Please check the transfer details and confirm.'),
      confirmText: i18n('Confirm'),
      cancelText: i18n('Cancel'),
      onConfirm: () => this.handleTransferConfirm()
    }
    confirmService.push(confirm)
  }

  get workOrder () {
    const location = this.location
    if (!location || !location.type) return
    return (location.type.key === 'production') &&
      <SearchPicker
        name="workOrder"
        value={this.state.workOrder}
        options={this.workOrderOptions}
        label={i18n("Work order")}
        onSearch={this.handleWorkOrderSearch}
        onChange={this.handleInputChange}
        onSelect={this.handleWorkOrderSelect}
        onBlur={this.handleWorkOrderBlur} />
  }

  listLocations () {
    locationApi.list(locations => {
      if (!locations) return
      this.setState({ locations })
    })
  }

  readGeolocation () {
    geolocationService.access(coordinate => {
      if (!coordinate) return
      const { latitude, longitude } = coordinate
      this.setState({ latitude, longitude })
    })
  }

  componentDidMount () {
    this.listLocations()
    this.readGeolocation()
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="twoThird">
          <Card>
            <PageHeading
              text={i18n("Bulk Transfer")}
              doc='pallets-pallets-bulk-transfer-to-a-location' />
            <Row column={1}>
              <InputFile
                name="inventories"
                filename={this.state.filename}
                accept=".csv"
                label="Inventory list"
                onChange={this.handleFileChange} />
              <TemplateDownloader
                text="Dowload sample inventory list template"
                filename="inventories" />
              <TableHorizontal
                headers={this.tableHeaders}
                data={this.state.pallets}
                onActionClick={this.handleActionClick} />
            </Row>
            <Row column={2}>
              <Select
                name="location"
                value={this.state.location}
                options={this.locationSelectOptions}
                label={i18n("Location")}
                onChange={this.handleInputChange} />
              <InputText
                name="bin"
                value={this.state.bin}
                label={i18n("Bin")}
                onChange={this.handleInputChange} />
              {this.workOrder}
            </Row>
            <CardButton
              proceedText={i18n("Transfer")}
              onProceedClick={this.handleTransferClick} />
          </Card>
        </ContentMain>
      </ContentLayout>
    )
  }
}
